import { createContext, useCallback, useMemo, useState, type ReactNode } from 'react';
import { Snackbar, type SnackbarNotice } from '@citibox-panel/ui';

export interface ErrorNoticeContextValue {
  // Raises (or replaces) the notice of one source. The `id` IS the source — the failing section,
  // not the occurrence — so a section that fails twice never stacks two copies of one sentence.
  notify: (id: string, message: string) => void;
  dismiss: (id: string) => void;
}

// The panel's second React context, and the first in this module. The tab seam next door
// deliberately has none — its state lives in the URL — and a notice must NOT: it is about one
// attempt, so a refresh or a shared link that resurrected it would report a failure that never
// happened in that session.
export const ErrorNoticeContext = createContext<ErrorNoticeContextValue | null>(null);

export interface ErrorNoticeProviderProps {
  children: ReactNode;
}

// ONE host for every error notice in the module (ADR 0021), mounted by `SearchShell` — the only
// common ancestor of the callers. Not one per slice: MUI does not stack independent `Snackbar`s, so
// a second slice raising its own would land on top of the first and hide it, and two failures at
// once is a real case now that a notice survives until it is dismissed.
export function ErrorNoticeProvider({ children }: ErrorNoticeProviderProps) {
  const [notices, setNotices] = useState<readonly SnackbarNotice[]>([]);

  const notify = useCallback((id: string, message: string) => {
    setNotices((prev) => {
      const current = prev.find((notice) => notice.id === id);
      // Same source, same sentence: return the very same array. Callers report from an effect over
      // a STANDING error state, so a new array on every render would be a re-render loop.
      if (current?.message === message) return prev;
      const next: SnackbarNotice = { id, severity: 'error', message };
      return current === undefined
        ? [...prev, next]
        : prev.map((notice) => (notice.id === id ? next : notice));
    });
  }, []);

  const dismiss = useCallback((id: string) => {
    setNotices((prev) => prev.filter((notice) => notice.id !== id));
  }, []);

  const value = useMemo(() => ({ notify, dismiss }), [notify, dismiss]);

  return (
    <ErrorNoticeContext.Provider value={value}>
      {children}
      {/* Last, and outside everything the children render: the notice floats over the panel, and
          the façade is what guarantees it never leaves on a timer. */}
      <Snackbar notices={notices} onDismiss={dismiss} />
    </ErrorNoticeContext.Provider>
  );
}
